'use client';
import React, { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Capture the event as early as possible at module level
// so it isn't lost before React mounts
declare global {
  interface Window {
    __pwaInstallPrompt?: BeforeInstallPromptEvent;
  }
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Already running as standalone PWA — don't show
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }
    // Also check navigator.standalone for older iOS
    if ((navigator as Navigator & { standalone?: boolean }).standalone === true) {
      setIsInstalled(true);
      return;
    }

    // Check dismissed timestamp — reset after 7 days
    const dismissedAt = localStorage.getItem('pwa-install-dismissed-at');
    if (dismissedAt) {
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - parseInt(dismissedAt, 10) < sevenDays) {
        return;
      }
      // Expired — clear it so prompt can show again
      localStorage.removeItem('pwa-install-dismissed-at');
    }

    // Detect iOS Safari (no beforeinstallprompt support)
    const ua = navigator.userAgent;
    const iosDevice = /iphone|ipad|ipod/i.test(ua);
    const safariOnly = /safari/i.test(ua) && !/chrome|crios|fxios/i.test(ua);
    if (iosDevice && safariOnly) {
      setIsIOS(true);
      setShowBanner(true);
      return;
    }

    // If the event was already captured before this component mounted, use it
    if (window.__pwaInstallPrompt) {
      setDeferredPrompt(window.__pwaInstallPrompt);
      setShowBanner(true);
      return;
    }

    // Otherwise listen for it (handles cases where it fires after mount)
    const handler = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      window.__pwaInstallPrompt = promptEvent;
      setDeferredPrompt(promptEvent);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    setShowBanner(false);
    setDeferredPrompt(null);
    window.__pwaInstallPrompt = undefined;
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-install-dismissed-at', Date.now().toString());
  };

  if (!showBanner || isInstalled) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50 bg-card border border-violet-500/30 rounded-xl shadow-xl shadow-black/30 p-4">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">Install Glanz CRM</p>
          {isIOS ? (
            <p className="text-xs text-muted-foreground mt-0.5">
              Tap the <strong>Share</strong> button then{' '}
              <strong>&ldquo;Add to Home Screen&rdquo;</strong>
            </p>
          ) : (
            <p className="text-xs text-muted-foreground mt-0.5">
              Add to your home screen for quick access and offline use
            </p>
          )}
        </div>
        <button
          onClick={handleDismiss}
          // Icon-only control: an SVG has no accessible text, so the name has
          // to come from the label. Without this it is announced as just
          // "button" (WCAG 2.1 AA — Name, Role, Value / 4.1.2).
          aria-label="Dismiss install prompt"
          className="text-muted-foreground hover:text-foreground p-1 flex-shrink-0"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
            focusable="false"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {!isIOS && (
        <div className="flex gap-2 mt-3">
          <button
            onClick={handleInstall}
            className="flex-1 py-2 bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium rounded-lg transition-colors"
          >
            Install App
          </button>
          <button
            onClick={handleDismiss}
            className="flex-1 py-2 bg-surface hover:bg-surface/80 text-muted-foreground text-xs font-medium rounded-lg transition-colors"
          >
            Not Now
          </button>
        </div>
      )}

      {isIOS && (
        <div className="mt-3">
          <button
            onClick={handleDismiss}
            className="w-full py-2 bg-surface hover:bg-surface/80 text-muted-foreground text-xs font-medium rounded-lg transition-colors"
          >
            Got it
          </button>
        </div>
      )}
    </div>
  );
}
